<template>
  <div class="orders-container">
    <div class="header-section">
      <div class="header-bg"></div>
      <van-nav-bar 
        title="我的订单" 
        left-arrow 
        @click-left="goBack"
        :border="false"
        class="nav-bar"
      >
        <template #right>
          <van-icon name="replay" size="22" @click="refreshOrders" class="refresh-icon" />
        </template>
      </van-nav-bar>
      <div class="header-stats" v-if="!loading && orders.length > 0">
        <div class="stat-item animate-slide-up">
          <div class="stat-icon">
            <van-icon name="orders-o" size="20" />
          </div>
          <div class="stat-content">
            <span class="stat-value">{{ orders.length }}</span>
            <span class="stat-label">全部订单</span>
          </div>
        </div>
        <div class="stat-divider"></div>
        <div class="stat-item animate-slide-up" style="animation-delay: 0.1s">
          <div class="stat-icon">
            <van-icon name="passed" size="20" />
          </div>
          <div class="stat-content">
            <span class="stat-value">{{ paidCount }}</span>
            <span class="stat-label">已完成</span>
          </div>
        </div>
        <div class="stat-divider"></div>
        <div class="stat-item animate-slide-up" style="animation-delay: 0.2s">
          <div class="stat-icon">
            <van-icon name="clock-o" size="20" />
          </div>
          <div class="stat-content">
            <span class="stat-value">{{ unpaidCount }}</span>
            <span class="stat-label">待支付</span>
          </div>
        </div>
      </div>
    </div>

    <div class="content">
      <van-loading v-if="loading" size="32px" vertical class="loading-wrapper">
        <span class="loading-text">正在加载订单...</span>
      </van-loading>
      
      <div v-if="!loading && orders.length === 0" class="empty-wrapper">
        <div class="empty-illustration">
          <van-icon name="orders-o" class="empty-icon" />
        </div>
        <h3 class="empty-title">暂无订单记录</h3>
        <p class="empty-desc">您还没有任何购买记录</p>
        <van-button 
          type="primary" 
          round 
          size="large" 
          class="empty-btn"
          @click="$router.push('/vip')"
        >
          前往会员中心
        </van-button>
      </div>
      
      <div class="orders-list" v-if="!loading && orders.length > 0">
        <div class="list-header">
          <span>最近订单</span>
          <span class="update-time">更新时间：{{ currentTime }}</span>
        </div>
        
        <div 
          v-for="(order, index) in orders" 
          :key="order.id"
          class="order-card"
          :class="{ 'paid': isOrderPaid(order), 'unpaid': !isOrderPaid(order) }"
          :style="{ animationDelay: index * 0.1 + 's' }"
        >
          <div class="card-accent"></div>
          
          <div class="order-header">
            <div class="order-info">
              <div class="order-number-row">
                <span class="order-no">订单号</span>
                <span class="order-no-value">{{ order.order_no }}</span>
              </div>
              <div class="status-badge" :class="isOrderPaid(order) ? 'status-success' : 'status-pending'">
                <van-icon :name="isOrderPaid(order) ? 'passed' : 'clock-o'" size="14" />
                <span>{{ isOrderPaid(order) ? '支付成功' : '待支付' }}</span>
              </div>
            </div>
            <div class="order-time">{{ formatTime(order.create_time) }}</div>
          </div>
          
          <div class="order-body">
            <div class="package-section">
              <div class="package-icon">
                <van-icon name="gem-o" size="24" />
              </div>
              <div class="package-details">
                <div class="package-name">{{ order.package_name }}</div>
                <div class="package-price">
                  <span class="currency">¥</span>
                  <span class="amount">{{ parseFloat(order.price).toFixed(2) }}</span>
                </div>
              </div>
            </div>
            
            <div class="order-actions">
              <van-button 
                v-if="!isOrderPaid(order)" 
                type="primary" 
                size="small" 
                round
                class="action-btn"
                @click="repay(order)"
              >
                <van-icon name="wechat" size="16" />
                立即支付
              </van-button>
              <van-button 
                v-else 
                type="default" 
                size="small" 
                round
                class="completed-btn"
                disabled
              >
                <van-icon name="passed" size="16" />
                已完成
              </van-button>
            </div>
          </div>
          
          <div class="order-footer" v-if="order.poetry_topic_id || order.pay_time">
            <div class="footer-divider"></div>
            <div class="order-meta">
              <span v-if="order.poetry_topic_id" class="meta-item">
                <van-icon name="description" size="12" />
                关联话题：{{ order.poetry_topic_id }}
              </span>
              <span v-if="order.pay_time" class="meta-item">
                <van-icon name="paid" size="12" />
                支付时间：{{ formatTime(order.pay_time) }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Toast } from 'vant'
import { syncOrderPayAfterWxSuccess } from '@/utils/wxpay'

export default {
  name: 'Orders',
  data() {
    return {
      orders: [],
      loading: true
    }
  },
  computed: {
    user() {
      return this.$store.state.user || {}
    },
    paidCount() {
      return this.orders.filter((order) => this.isOrderPaid(order)).length
    },
    unpaidCount() {
      return this.orders.filter((order) => !this.isOrderPaid(order)).length
    },
    currentTime() {
      const now = new Date()
      const hours = String(now.getHours()).padStart(2, '0')
      const minutes = String(now.getMinutes()).padStart(2, '0')
      return `${hours}:${minutes}`
    }
  },
  mounted() {
    this.loadOrders()
  },
  methods: {
    isOrderPaid(order) {
      if (!order) {
        return false
      }
      const payStatus = parseInt(order.pay_status, 10)
      const status = parseInt(order.status, 10)
      return payStatus === 1 || status === 1
    },
    goBack() {
      const backRoute = this.$store.getters.smartBackRoute
      this.$router.push(backRoute)
    },
    async loadOrders() {
      this.loading = true
      try {
        const response = await this.$http.get('/api/orders/my')
        this.orders = response.data.data.orders || []
      } catch (err) {
        Toast.fail(err.message || '加载订单失败')
      } finally {
        this.loading = false
      }
    },
    async refreshOrders() {
      Toast.loading({ message: '正在同步支付状态…', forbidClick: true, duration: 0 })
      try {
        const res = await this.$http.post('/api/orders/sync-all-pending')
        if (res.data.code === 200 && res.data.data && res.data.data.orders) {
          this.orders = res.data.data.orders
        } else {
          await this.loadOrders()
        }
        Toast.clear()
        Toast.success('刷新成功')
      } catch (err) {
        Toast.clear()
        await this.loadOrders()
        Toast.fail(err.message || '刷新失败')
      }
    },
    formatTime(timestamp) {
      if (!timestamp || timestamp === 0) return '未知'
      const date = new Date(timestamp * 1000)
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      const hour = String(date.getHours()).padStart(2, '0')
      const minute = String(date.getMinutes()).padStart(2, '0')
      return `${month}-${day} ${hour}:${minute}`
    },
    async repay(order) {
      try {
        this.$loading.show('正在创建支付订单...')
        
        const response = await this.$http.post('/api/vip/buy', {
          package_id: order.package_id
        })
        
        this.$loading.hide()
        
        const orderNo = response.data.data.order_no
        const jsApiParams = response.data.data.jsApiParams
        
        if (typeof WeixinJSBridge !== 'undefined') {
          WeixinJSBridge.invoke('getBrandWCPayRequest', jsApiParams, async (res) => {
            if (res.err_msg === 'get_brand_wcpay_request:ok') {
              Toast.loading({ message: '正在确认支付…', forbidClick: true, duration: 0 })
              await syncOrderPayAfterWxSuccess(this.$http, orderNo)
              await this.$store.dispatch('fetchUserInfo')
              Toast.clear()
              Toast.success('支付成功')
              this.loadOrders()
            } else if (res.err_msg === 'get_brand_wcpay_request:cancel') {
              Toast('支付已取消')
            } else {
              Toast.fail('支付失败')
            }
          })
        } else {
          Toast.fail('请在微信中打开')
        }
      } catch (err) {
        this.$loading.hide()
        Toast.fail(err.message || '支付失败')
      }
    }
  }
}
</script>

<style scoped>
::v-deep .van-nav-bar .van-icon{
  color: white !important;
}
::v-deep .van-nav-bar__title{
  color: white !important;
}
::v-deep .van-nav-bar__content {
  margin: 0.5rem 0 0 0;
}
.orders-container {
  min-height: 100vh;
  background: linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%);
}

.header-section {
  position: relative;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 50%, #6366f1 100%);
  padding-bottom: 10px;
  /* border-radius: 0 0 32px 32px; */
  overflow: hidden;
}

.header-bg {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.15) 0%, rgba(255, 255, 255, 0.05) 100%);
  backdrop-filter: blur(10px);
}

.header-decoration {
  position: absolute;
  top: -50px;
  right: -50px;
  width: 200px;
  height: 200px;
  background: radial-gradient(circle, rgba(255, 255, 255, 0.1) 0%, transparent 70%);
  border-radius: 50%;
}

.nav-bar {
  position: relative;
  z-index: 1;
  background: transparent !important;
  padding-top: env(safe-area-inset-top);
}

.nav-bar .van-nav-bar__title {
  color: white !important;
  font-weight: 700;
  font-size: 18px;
}

.nav-bar .van-nav-bar__left,
.nav-bar .van-nav-bar__right {
  color: white !important;
}

.nav-bar .van-nav-bar__left .van-icon,
.nav-bar .van-nav-bar__right .van-icon {
  color: white !important;
}

.refresh-icon {
  transition: transform 0.3s ease;
}

.refresh-icon:active {
  transform: rotate(180deg);
}

.header-stats {
  position: relative;
  z-index: 1;
  display: flex;
  justify-content: space-around;
  align-items: center;
  padding: 20px 24px;
  margin-top: 8px;
  background: rgba(255, 255, 255, 0.1);
  margin: 0 16px;
  margin-top: 16px;
  border-radius: 16px;
  backdrop-filter: blur(10px);
}

.stat-item {
  display: flex;
  align-items: center;
  gap: 12px;
  opacity: 0;
  transform: translateY(20px);
}

.stat-item.animate-slide-up {
  animation: slideUp 0.5s ease forwards;
}

@keyframes slideUp {
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.stat-icon {
  width: 44px;
  height: 44px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
}

.stat-content {
  display: flex;
  flex-direction: column;
}

.stat-value {
  font-size: 24px;
  font-weight: 700;
  color: white;
  line-height: 1;
}

.stat-label {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.85);
  font-weight: 500;
}

.stat-divider {
  width: 1px;
  height: 40px;
  background: rgba(255, 255, 255, 0.2);
}

.content {
  padding: 20px 16px;
  margin-top: -20px;
}

.loading-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 60vh;
}

.loading-text {
  margin-top: 12px;
  font-size: 14px;
  color: #666;
}

.empty-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 60vh;
  padding: 40px 20px;
  text-align: center;
}

.empty-illustration {
  width: 120px;
  height: 120px;
  border-radius: 50%;
  background: linear-gradient(135deg, #667eea20, #764ba220);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 24px;
  box-shadow: 0 8px 32px rgba(102, 126, 234, 0.15);
}

.empty-icon {
  font-size: 56px;
  color: #667eea;
  opacity: 0.6;
}

.empty-title {
  font-size: 20px;
  font-weight: 600;
  color: #1a1a2e;
  margin-bottom: 8px;
}

.empty-desc {
  font-size: 14px;
  color: #666;
  margin-bottom: 32px;
}

.empty-btn {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border: none;
  box-shadow: 0 4px 16px rgba(102, 126, 234, 0.3);
  font-weight: 600;
}

.empty-btn:hover {
  box-shadow: 0 6px 20px rgba(102, 126, 234, 0.4);
  transform: translateY(-2px);
}

.orders-list {
  margin-top: 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.list-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
  padding: 0 4px;
}

.list-header span:first-child {
  font-size: 16px;
  font-weight: 600;
  color: #1a1a2e;
}

.update-time {
  font-size: 12px;
  color: #999;
}

.order-card {
  position: relative;
  background: white;
  border-radius: 16px;
  padding: 20px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
  overflow: hidden;
  animation: slideUp 0.4s ease-out forwards;
  opacity: 0;
  transform: translateY(20px);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

@keyframes slideUp {
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.order-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
}

.order-card:active {
  transform: translateY(-2px);
}

.card-accent {
  position: absolute;
  top: 0;
  left: 0;
  width: 4px;
  height: 100%;
  transition: width 0.3s ease;
}

.order-card.paid .card-accent {
  background: linear-gradient(180deg, #52c41a, #73d13d);
}

.order-card.unpaid .card-accent {
  background: linear-gradient(180deg, #fa8c16, #ffa940);
}

.order-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 16px;
  padding-left: 12px;
}

.order-info {
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
}

.order-number-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.order-no {
  font-size: 12px;
  color: #999;
  font-weight: 500;
}

.order-no-value {
  font-size: 13px;
  color: #1a1a2e;
  font-weight: 600;
  font-family: 'Courier New', monospace;
  letter-spacing: 0.5px;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  width: fit-content;
  transition: transform 0.2s ease;
}

.status-badge:active {
  transform: scale(0.95);
}

.status-success {
  background: linear-gradient(135deg, #f6ffed, #e6fffb);
  color: #52c41a;
  border: 1px solid #b7eb8f;
}

.status-pending {
  background: linear-gradient(135deg, #fff7e6, #fff1e6);
  color: #fa8c16;
  border: 1px solid #ffd591;
}

.order-time {
  font-size: 12px;
  color: #999;
  padding-left: 12px;
}

.order-body {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  padding-left: 12px;
}

.package-section {
  display: flex;
  align-items: center;
  gap: 16px;
  flex: 1;
}

.package-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: linear-gradient(135deg, #667eea20, #764ba220);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #667eea;
}

.package-details {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.package-name {
  font-size: 16px;
  font-weight: 600;
  color: #1a1a2e;
}

.package-price {
  display: flex;
  align-items: baseline;
  gap: 2px;
}

.currency {
  font-size: 14px;
  font-weight: 600;
  color: #667eea;
}

.amount {
  font-size: 24px;
  font-weight: 700;
  color: #667eea;
  line-height: 1;
}

.order-actions {
  display: flex;
  gap: 8px;
  flex-shrink: 0;
}

.action-btn {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border: none;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
  font-weight: 600;
  padding-left: 16px;
  padding-right: 16px;
  transition: all 0.3s ease;
}

.action-btn:hover {
  box-shadow: 0 6px 16px rgba(102, 126, 234, 0.4);
  transform: translateY(-2px);
}

.action-btn:active {
  transform: translateY(0);
  box-shadow: 0 2px 8px rgba(102, 126, 234, 0.3);
}

.completed-btn {
  background: #f5f5f5;
  border: none;
  color: #999;
  font-weight: 500;
}

.order-footer {
  padding-left: 12px;
}

.footer-divider {
  height: 1px;
  background: #f0f0f0;
  margin-bottom: 12px;
}

.order-meta {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.meta-item {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #999;
}

.van-button--small {
  height: 32px;
  line-height: 30px;
  font-size: 13px;
}

@media screen and (max-width: 375px) {
  .order-body {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }
  
  .order-actions {
    width: 100%;
    justify-content: flex-end;
  }
}
</style>
